import { SortAscending, SortDescending } from "@phosphor-icons/react"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useMemo, memo, useCallback } from "react"
import Loader from "./Loader"
import Pagination from "./Pagination"
import Filter from "./TableFilters"
import { formatISODate } from "../utils/datetime"
import { useNavigate } from "react-router-dom"

const columnHelper = createColumnHelper()

const AuditLogsTable = ({
  data,
  loading,
  onPaginationChange,
  pagination,
  pageCount,
  filters,
  setFilters,
}) => {
  const navigate = useNavigate()

  const handleViewDetails = useCallback(
    (rowData) => {
      navigate("/audit-log", {
        state: { ...rowData },
      })
    },
    [navigate]
  )

  const columns = useMemo(
    () => [
      columnHelper.accessor("accessionNo", {
        header: "Accession #",
        id: "accessionNo",
        sortDescFirst: false,
        size: 100,
        meta: {
          filterVariant: "text",
        },
      }),
      columnHelper.accessor("PrlmnryRcvd", {
        header: "Preliminary Report received from PACS",
        id: "PrlmnryRcvd",
        size: 250,
        cell: (info) =>
          formatISODate(info.renderValue(), "MM/dd/yyyy hh:mm:ss a") || "",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row["alUPSP.UpdatedPrlmnrySent"], {
        header: "Updated Preliminary report sent to PACS",
        id: "alUPSP.UpdatedPrlmnrySent",
        size: 250,
        cell: (info) =>
          formatISODate(info.renderValue(), "MM/dd/yyyy hh:mm:ss a") || "",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("Actions", {
        header: "Actions",
        id: "actions",
        size: 30,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <button
              className="bg-secondary hover:bg-secondary_dark px-2 py-1 text-sm text-white rounded"
              onClick={() => handleViewDetails(row.original)}
            >
              View Details
            </button>
          </div>
        ),
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
    ],
    []
  )

  const tableConfig = useMemo(
    () => ({
      columns,
      data,
      manualPagination: true,
      manualSorting: true,
      manualFiltering: true,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange,
      onColumnFiltersChange: setFilters,
      pageCount,
      state: {
        pagination,
        columnFilters: filters,
      },
    }),
    [
      columns,
      data,
      onPaginationChange,
      setFilters,
      pageCount,
      pagination,
      filters,
    ]
  )

  const table = useReactTable(tableConfig)

  const TableHeader = useMemo(
    () => (
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((column) => (
              <th
                key={column.id}
                className="py-2 px-4 border-b border-gray-200 bg-gray-100 align-top whitespace"
                style={{ width: `${column.getSize()}px` }}
              >
                <div className="flex flex-col justify-start items-start">
                  <button
                    {...{
                      className: column.column.getCanSort()
                        ? "w-full select-none cursor-pointer flex items-center gap-1"
                        : "w-full",
                      onClick: column.column.getToggleSortingHandler(),
                    }}
                  >
                    {flexRender(
                      column.column.columnDef.header,
                      column.getContext()
                    )}
                    {{
                      asc: <SortAscending className="h-4 w-4" />,
                      desc: <SortDescending className="h-4 w-4" />,
                    }[column.column.getIsSorted()] ?? null}
                  </button>
                  {column.column.getCanFilter() ? (
                    <div className="mt-2">
                      <Filter column={column.column} />
                    </div>
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
    ),
    [table]
  )

  const TableBody = useMemo(() => {
    if (loading) {
      return (
        <tbody>
          <tr>
            <td colSpan={table.getAllColumns().length}>
              <div className="py-2">
                <Loader />
              </div>
            </td>
          </tr>
        </tbody>
      )
    }

    return (
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id} className="hover:bg-gray-100">
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                className="px-4 py-2 border-b border-gray-200 content-center"
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    )
  }, [loading, table, data])

  return (
    <>
      <div className="p-4">
        <table className="min-w-full bg-white border border-gray-200">
          {TableHeader}
          {TableBody}
        </table>
        {!loading && data.length > 0 && <Pagination tableLib={table} />}
        {!loading && data.length === 0 && (
          <div className="mt-3 text-center">No Logs found</div>
        )}
      </div>
    </>
  )
}

export default memo(AuditLogsTable)
